import { useCallback, useEffect, useRef, useState } from 'react';
import {
  isGoogleMapsMissingApiKeyError,
  loadGoogleMapsScript,
  type GoogleAddressComponent,
  type GoogleMapsNamespace,
  type GooglePlacePrediction,
} from '~/app/lib/google-maps';

export type AddressAutocompleteSelection = {
  address: string;
  latitude: number;
  longitude: number;
  cityName?: string;
  provinceName?: string;
  postalCode?: string;
};

type AddressAutocompleteOption = {
  label: string;
  value: string;
  placeId: string;
};

type UseSiteAddressAutocompleteParams = {
  searchValue: string;
  missingKeyText: string;
  loadFailedText: string;
  onAddressSelect: (
    selection: AddressAutocompleteSelection,
  ) => Promise<void> | void;
};

function getAddressComponentValue(
  addressComponents: GoogleAddressComponent[] | undefined,
  componentTypes: string[],
): string | undefined {
  return addressComponents?.find((component) =>
    componentTypes.every((componentType) =>
      component.types.includes(componentType),
    ),
  )?.long_name;
}

export function useSiteAddressAutocomplete({
  searchValue,
  missingKeyText,
  loadFailedText,
  onAddressSelect,
}: UseSiteAddressAutocompleteParams) {
  const normalizedSearchValue = searchValue.trim();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [options, setOptions] = useState<AddressAutocompleteOption[]>([]);
  const [resolvedSearchValue, setResolvedSearchValue] = useState('');
  const googleMapsRef = useRef<GoogleMapsNamespace | null>(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (!normalizedSearchValue) {
      requestIdRef.current += 1;
      return;
    }

    const timeoutId = window.setTimeout(() => {
      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;
      setResolvedSearchValue(normalizedSearchValue);
      setOptions([]);
      setErrorMessage(null);
      setIsLoading(true);

      void loadGoogleMapsScript()
        .then((googleMaps) => {
          googleMapsRef.current = googleMaps;

          const autocompleteService =
            new googleMaps.maps.places.AutocompleteService();

          autocompleteService.getPlacePredictions(
            {
              input: normalizedSearchValue,
              componentRestrictions: { country: 'fr' },
            },
            (predictions: GooglePlacePrediction[] | null, status: string) => {
              if (requestIdRef.current !== requestId) {
                return;
              }

              if (status !== 'OK' || !predictions) {
                setOptions([]);
                setIsLoading(false);
                return;
              }

              setOptions(
                predictions.map((prediction) => ({
                  label: prediction.description,
                  value: prediction.description,
                  placeId: prediction.place_id,
                })),
              );
              setErrorMessage(null);
              setIsLoading(false);
            },
          );
        })
        .catch((error: unknown) => {
          setOptions([]);
          setIsLoading(false);
          setErrorMessage(
            isGoogleMapsMissingApiKeyError(error)
              ? missingKeyText
              : loadFailedText,
          );
        });
    }, 250);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [loadFailedText, missingKeyText, normalizedSearchValue]);

  const handleSelect = useCallback(
    async (_value: string, option: { placeId?: string }) => {
      const placeId = option.placeId;

      if (!placeId) {
        return;
      }

      setIsLoading(true);

      try {
        const googleMaps =
          googleMapsRef.current ?? (await loadGoogleMapsScript());
        googleMapsRef.current = googleMaps;

        const geocoder = new googleMaps.maps.Geocoder();

        await new Promise<void>((resolve, reject) => {
          geocoder.geocode({ placeId }, (results, status) => {
            if (status !== 'OK' || !results?.[0]) {
              reject(new Error('Failed to geocode place'));
              return;
            }

            const result = results[0];
            const cityName =
              getAddressComponentValue(result.address_components, [
                'locality',
              ]) ??
              getAddressComponentValue(result.address_components, [
                'postal_town',
              ]) ??
              getAddressComponentValue(result.address_components, [
                'administrative_area_level_3',
              ]);
            const provinceName = getAddressComponentValue(
              result.address_components,
              ['administrative_area_level_1'],
            );
            const postalCode =
              result.address_components?.find((component) =>
                component.types.includes('postal_code'),
              )?.short_name ?? result.formatted_address.match(/\b\d{5}\b/)?.[0];

            void Promise.resolve(
              onAddressSelect({
                address: result.formatted_address,
                latitude: result.geometry.location.lat(),
                longitude: result.geometry.location.lng(),
                cityName,
                provinceName,
                postalCode,
              }),
            ).then(resolve, reject);
          });
        });

        setErrorMessage(null);
      } catch {
        setErrorMessage(loadFailedText);
      } finally {
        setIsLoading(false);
      }
    },
    [loadFailedText, onAddressSelect],
  );

  return {
    errorMessage:
      resolvedSearchValue === normalizedSearchValue ? errorMessage : null,
    isLoading:
      resolvedSearchValue === normalizedSearchValue ? isLoading : false,
    options: resolvedSearchValue === normalizedSearchValue ? options : [],
    handleSelect,
  };
}
